import { useForm } from "react-hook-form";
import { ScaleLoader } from "react-spinners";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as EyeOpen } from "assets/svgs/eye-open.svg";
import { ReactComponent as EyeClose } from "assets/svgs/eye-close.svg";

const INPUT_BASE =
  "w-full h-[44px] px-3 bg-shade-50 border rounded-ns text-[15px] text-ink outline-none transition-colors duration-200 placeholder-shade-900";
const INPUT_OK =
  "border-rule-strong focus:border-accent focus:shadow-[0_0_0_3px_rgba(123,94,167,0.12)]";
const INPUT_ERR = "border-danger";

const OTP_INPUT_BASE =
  "w-11 h-11 text-center text-[18px] font-semibold border-2 rounded-ns bg-shade-50 text-ink outline-none transition-colors duration-200";
const OTP_INPUT_OK =
  "border-rule-strong focus:border-accent focus:shadow-[0_0_0_3px_rgba(123,94,167,0.12)]";
const OTP_INPUT_ERR = "border-danger";

const NewPassword = ({ onSubmit, loading }) => {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const password = watch("password");
  const [hidePassword, setHidePassword] = useState(true);
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true);

  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !event.target.value && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleInput = (index) => {
    if (index < 5 && inputRefs.current[index].value) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    pasted.split("").forEach((char, i) => {
      if (inputRefs.current[i]) {
        inputRefs.current[i].value = char;
        inputRefs.current[i].dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const hasOtpError =
    errors.code0 || errors.code1 || errors.code2 ||
    errors.code3 || errors.code4 || errors.code5;

  return (
    <>
      <div className="mb-5">
        <h2 className="text-[20px] font-bold text-ink mb-1">Set a new password</h2>
        <p className="text-xs text-muted">
          Enter the code from your email and choose a new password.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* OTP */}
        <div className="mb-4">
          <label className="block text-[12px] font-medium text-muted mb-[5px]">
            Verification code <span className="text-danger">*</span>
          </label>
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                {...register(`code${index}`, { required: true, maxLength: 1 })}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                onInput={() => handleInput(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className={`${OTP_INPUT_BASE} ${hasOtpError ? OTP_INPUT_ERR : OTP_INPUT_OK}`}
                ref={(el) => {
                  inputRefs.current[index] = el;
                  register(`code${index}`).ref(el);
                }}
              />
            ))}
          </div>
          {hasOtpError && (
            <p role="alert" className="text-danger text-[11px] mt-1">
              Please enter the complete 6-digit code.
            </p>
          )}
        </div>

        {/* New password */}
        <div className="mb-3">
          <label
            htmlFor="password"
            className="block text-[12px] font-medium text-muted mb-[5px]"
          >
            New password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="password"
              type={hidePassword ? "password" : "text"}
              {...register("password", {
                required: "Password is required",
                pattern: {
                  value:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/,
                  message:
                    "8+ chars with uppercase, lowercase, number & special character",
                },
              })}
              placeholder="Create a new password"
              aria-describedby={errors.password ? "password-error" : undefined}
              className={`${INPUT_BASE} pr-10 ${errors.password ? INPUT_ERR : INPUT_OK}`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setHidePassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-muted"
              aria-label={hidePassword ? "Show password" : "Hide password"}
            >
              {hidePassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" role="alert" className="text-danger text-[11px] mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm password */}
        <div className="mb-5">
          <label
            htmlFor="confirmPassword"
            className="block text-[12px] font-medium text-muted mb-[5px]"
          >
            Confirm new password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={hideConfirmPassword ? "password" : "text"}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) => value === password || "Passwords do not match",
              })}
              placeholder="Re-enter your new password"
              aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
              className={`${INPUT_BASE} pr-10 ${errors.confirmPassword ? INPUT_ERR : INPUT_OK}`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setHideConfirmPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-muted"
              aria-label={hideConfirmPassword ? "Show password" : "Hide password"}
            >
              {hideConfirmPassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p id="confirmPassword-error" role="alert" className="text-danger text-[11px] mt-1">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full h-12 rounded-ns bg-accent text-white text-[15px] font-bold relative border border-rule-strong  transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className={loading ? "opacity-0" : "opacity-100"}>Update password →</span>
          {loading && (
            <ScaleLoader
              loading
              height={16}
              color="white"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          )}
        </button>

        <p className="mt-4 text-center text-[13px] text-muted">
          Back to{" "}
          <a href="/login" className="text-accent font-semibold hover:underline">
            Login
          </a>
        </p>
      </form>
    </>
  );
};

export default NewPassword;
